import { Component } from '@theme/component';

/**
 * Native mega menus and a modal mobile drawer. Search/cart use Horizon components.
 * @typedef {object} Refs
 * @property {HTMLDialogElement} mobileDialog
 * @property {HTMLButtonElement} menuButton
 * @extends {Component<Refs>}
 */
class NarsonRaceHeader extends Component {
  requiredRefs = ['mobileDialog', 'menuButton'];
  #controller;
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#bindListeners();
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
      document.body.style.setProperty('--header-height', `${height}px`);
      document.body.style.setProperty('--header-group-height', `${height}px`);
    });
    this.#resizeObserver.observe(this);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bindListeners();
  }

  #bindListeners() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('focusout', this.#leaveMenu, { signal });
    document.addEventListener('click', this.#outsideClick, { signal });
    document.addEventListener('keydown', this.#escapeMenu, { signal });
    this.refs.mobileDialog.addEventListener('close', this.#restoreMenuFocus, { signal });
    matchMedia('(min-width: 1180px)').addEventListener('change', this.#desktopChanged, { signal });

    // Native details remains keyboard operable; hover is only a fine-pointer enhancement.
    for (const menu of this.querySelectorAll('.race-menu')) {
      if (typeof menu.addEventListener !== 'function') continue;
      menu.addEventListener('mouseenter', () => {
        if (matchMedia('(min-width: 1180px) and (hover: hover)').matches) {
          this.#closeMenus();
          menu.open = true;
        }
      }, { signal });
      menu.addEventListener('mouseleave', () => {
        if (!menu.contains(document.activeElement)) menu.open = false;
      }, { signal });
    }
  }

  disconnectedCallback() {
    this.refs.mobileDialog?.close();
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    super.disconnectedCallback();
  }

  /** Opens the mobile drawer with native focus containment. */
  openMobileMenu() {
    this.#closeMenus();
    if (!this.refs.mobileDialog.open) this.refs.mobileDialog.showModal();
    this.refs.menuButton.setAttribute('aria-expanded', 'true');
  }

  /** Closes the mobile drawer. */
  closeMobileMenu() {
    this.refs.mobileDialog.close();
  }

  /** @param {MouseEvent} event - Dialog backdrop click. */
  handleBackdrop(event) {
    if (event.target !== this.refs.mobileDialog) return;
    const rect = this.refs.mobileDialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
      this.closeMobileMenu();
    }
  }

  /** Swaps the two merchant announcements after an explicit button click. */
  swapAnnouncement() {
    const primary = this.querySelector('.race-header__announcement-text');
    const alternate = this.querySelector('.race-header__announcement-alt');
    if (!primary || !alternate) return;
    const next = alternate.textContent;
    alternate.textContent = primary.textContent;
    primary.textContent = next;
  }

  #restoreMenuFocus = () => {
    this.refs.menuButton.setAttribute('aria-expanded', 'false');
    this.refs.menuButton.focus();
  };

  #desktopChanged = (event) => {
    if (event.matches && this.refs.mobileDialog.open) this.closeMobileMenu();
    this.#closeMenus();
  };

  #leaveMenu = (event) => {
    if (event.relatedTarget instanceof Node && !this.contains(event.relatedTarget)) this.#closeMenus();
  };

  #outsideClick = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) this.#closeMenus();
  };

  #escapeMenu = (event) => {
    if (event.key !== 'Escape' || this.refs.mobileDialog.open) return;
    const openMenu = this.querySelector('.race-menu[open]');
    if (!openMenu) return;
    event.preventDefault();
    openMenu.querySelector('summary')?.focus();
    this.#closeMenus();
  };

  #closeMenus() {
    for (const menu of this.querySelectorAll('.race-menu[open]')) {
      menu.open = false;
    }
  }
}

if (!customElements.get('narson-race-header')) customElements.define('narson-race-header', NarsonRaceHeader);
